import { forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { TileStatus } from "@/modules/core/components/settings.ts";
import { type ContentProps, ContentTile, Tile } from "@/modules/core/components/Tile.tsx";
import style from "@/modules/core/styles/Tile.module.scss";

import {
  getIconElementForPlaybook,
  isPlaybookOnlyExecutableInGenomeViewerContext,
} from "../../../../app-utils/playbook.js";
import type { Configuration, Playbook } from "../../../../graphql.js";
import { isPlaybookLicensed } from "../../../../redux/modules/config.js";
import { openPlaybook } from "../../../../redux/modules/playbook-execution.js";
import { type IAppState } from "../../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../../redux/redux-utils.js";
import { ENTITY_TYPE, FEATURE_DISPLAY_NAME } from "../../../../settings/settings.js";
import { ProtectedResourceLogo } from "../../ProtectedResourceLogo.js";

interface PlaybookTileProps {
  playbook: Playbook;
  onPress?: () => void;
  inMenu?: boolean;
  asContentTile?: boolean;
}

const PlaybookTile = forwardRef<HTMLDivElement, PlaybookTileProps>(function PlaybookTile(props, ref) {
  const { playbook } = props;
  const dispatch = useDispatch<ReduxDispatch>();
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration);
  const licensed = isPlaybookLicensed(configuration, props.playbook);

  if (!props.playbook) return null;

  const contentProps: ContentProps = {
    title: playbook.name,
    subtitle: playbook.nameSuffix,
    type: props.inMenu ? undefined : playbook.categories.map((c) => c.name).join(", "),
    statusDisplayName: FEATURE_DISPLAY_NAME[ENTITY_TYPE.PLAYBOOK],
    icon: <img width={75} src={getIconElementForPlaybook(playbook).large} alt={"Playbook icon"} />,
  };

  let protectedStatus: TileStatus = TileStatus.Normal;
  if (!licensed) {
    protectedStatus = TileStatus.NotLicensed;
  } else if (playbook.protectedPlaybook) {
    protectedStatus = TileStatus.RestrictedVisibility;
  } else if (isPlaybookOnlyExecutableInGenomeViewerContext(playbook)) {
    protectedStatus = TileStatus.RequiresContext;
  } else if (!playbook.enabled) {
    protectedStatus = TileStatus.Disabled;
  }

  return props.asContentTile ? (
    <ContentTile {...contentProps}>
      {playbook.description}
      {playbook.standard && <ProtectedResourceLogo size={18} className={style.selector_icon_tile} />}
    </ContentTile>
  ) : (
    <Tile
      {...contentProps}
      protectionStatus={protectedStatus}
      ref={ref}
      onPress={() => {
        props.onPress?.();
        dispatch(openPlaybook(playbook));
      }}
    >
      {playbook.description}
      {playbook.standard && <ProtectedResourceLogo size={16} className={style.selector_icon_tile} />}
    </Tile>
  );
});

export { PlaybookTile };
